import {
  Component,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
  SimpleChanges,
  ViewChild,
  Input,
  ViewEncapsulation,
  EventEmitter,
  Output,
  AfterViewInit,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { BoobleModalService } from 'booble-modal';
import { ActivatedRoute } from '@angular/router';
import { AdminOrderEditModal } from '../../home/modals/order-edit-modal/admin-order-edit-modal.component';
import { AdminOrderDetailModalComponent } from '../../home/modals/order-details-modal/admin-order-detail-modal.component';
import { QuotationReconciliationModalComponent } from '../../home/modals/quotation-reconciliation-modal/quotation-reconciliation-modal.component';
import { OrderReviewModalComponent } from '../../home/modals/order-review-modal/order-review-modal.component';
import { TreasuryInfoComponent } from '../../home/modals/treasury-info/treasury-info.component';
import { ExchangeInfoComponent } from '../../home/modals/exchange-info/exchange-info.component';
import { SettlementInfoComponent } from '../../home/modals/settlement-info/settlement-info.component';
import { PaymentConfirmComponent } from '../../home/modals/payment-confirm/payment-confirm.component';
import { FinancialReconciliationModalComponent } from '../../home/modals/financial-reconciliation-modal/financial-reconciliation-modal.component';
import { AttachProofOperationComponent } from '../../home/modals/attach-proof-operation/attach-proof-operation.component';
import { SettlementInfoConfirmComponent } from '../../home/modals/settlement-info-confirm/settlement-info-confirm.component';
import { SettlementDocumentsComponent } from '../modals/settlement-documents/settlement-documents.component';
import { ExchangeDetailsComponent } from '../modals/exchange-details/exchange-details.component';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';
import { SendContractComponent } from '../../home/modals/send-contract/send-contract.component';

@Component({
  selector: 'app-table-history',
  templateUrl: './table-history.component.html',
  styleUrls: ['./table-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableHistoryComponent implements OnInit, OnChanges, AfterViewInit {
  rType: string = '';
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input() displayedColumns: string[];
  @Input() pageNumber: number = 0;
  @Input() pageSize: number = 10;
  @Input() totalElements: number;

  @Output() pageChange = new EventEmitter<{
    pageNumber: number;
    pageSize: number;
  }>();
 
  selection = new SelectionModel<InboundOrderDto>(true, []);
  dataSource: MatTableDataSource<InboundOrderDto>;

  @Input() data: ResponseTableModel<InboundOrderDto[]>;

  constructor(
    private modalService: BoobleModalService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const rType = params.get('rType');
      if (rType) this.rType = rType;
    });
    this.initializeTable();
  }

  ngAfterViewInit(): void {
    this.initializeTableSort();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      const currentData = changes['data'].currentValue;
      this.dataSource = new MatTableDataSource(currentData?.content || []);
      this.totalElements = currentData?.totalElements || 0;

      if (this.paginator) {
        this.paginator.pageIndex = this.pageNumber;
        this.paginator.length = this.totalElements;
      }
      this.initializeTableSort();
    }
  }

  initializeTableSort() {
    if (this.sort) {
      this.sort.active = 'id';
      this.sort.direction = 'desc';
      this.dataSource.sort = this.sort;
    }

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'cnpj':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount.cnpj
            : item.individualAccount?.cpf;
        case 'companyName':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount?.companyName
            : item.individualAccount?.name;
        case 'clientInfo':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount?.companyName
            : item.individualAccount?.name;
        default:
          return item[property] || '';
      }
    };
  }

  private initializeTable() {
    this.dataSource = new MatTableDataSource(this.data?.content || []);
    this.dataSource.paginator = this.paginator;

    this.paginator._intl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return '0 de ' + length;
      }

      length = Math.max(length, 0);
      const startIndex = page * pageSize;

      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;
      return `Exibindo ${endIndex} de ${length} ordens`;
    };
  }

  onPageChange(event: { pageIndex: number; pageSize: number }) {
    const { pageIndex, pageSize } = event;

    this.pageNumber = pageIndex;
    this.pageSize = pageSize;

    sessionStorage.setItem('orders-history-page-size', pageSize.toString());
    sessionStorage.setItem('orders-history-page-number', pageIndex.toString());

    this.pageChange.emit({ pageNumber: pageIndex, pageSize });
  }
  onRowToggled(row: any) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  goEditOrder(order: InboundOrderDto) {
    this.modalService.present(AdminOrderEditModal, order);
  }

  goOrderDetail(orderId: number) {
    this.modalService.present(AdminOrderDetailModalComponent, orderId);
  }

  conciliate(order: InboundOrderDto) {
    this.modalService.present(QuotationReconciliationModalComponent, order);
  }

  openOrderReviewModal(orderId: number) {
    this.modalService.present(OrderReviewModalComponent, orderId);
  }

  openAttachProofOperationModal(orderId: number) {
    this.modalService.present(AttachProofOperationComponent, orderId);
  }

  openSettlementDocumentsComponentModal(orderId: number) {
    this.modalService.present(SettlementDocumentsComponent, orderId);
  }

  openExchangeDetailsComponentModal(order: InboundOrderDto) {
    this.modalService.present(ExchangeDetailsComponent, order);
  }

  openTreasuryInfoModal(orderId: number) {
    this.modalService.present(TreasuryInfoComponent, orderId);
  }

  openExchangeInfoModal(order: InboundOrderDto) {
    this.modalService.present(ExchangeInfoComponent, order);
  }

  openSettlementConfirmModal(order: InboundOrderDto) {
    this.modalService.present(SettlementInfoConfirmComponent, order);
  }

  openSettlementInfoModal(order: InboundOrderDto) {
    this.modalService.present(SettlementInfoComponent, order);
  }

  openSendContractModal(order: InboundOrderDto) {
    this.modalService.present(SendContractComponent, order);
  }

  openPaymentConfirmModal(order: InboundOrderDto) {
    this.modalService.present(PaymentConfirmComponent, order);
  }
  action(order: InboundOrderDto) {
    if (order.statusFlow == 'PROCESSING') {
      this.openOrderReviewModal(order?.id);
    } else if (order.statusFlow == 'AVAILABLE_PAYMENT') {
      this.openFinancialReconciliationModal(order);
    } else if (order.statusFlow == 'AVAILABLE_QUOTATION') {
      this.conciliate(order);
    } else if (order.statusFlow == 'PENDING_INVOICE_INFORMATION') {
      this.goEditOrder(order);
    }
  }

  openFinancialReconciliationModal(order: InboundOrderDto) {
    this.modalService.present(FinancialReconciliationModalComponent, order);
  }
}
